/**
 * KPI Analytics tables (H4) — four stacked role-scoped cards instead of one
 * wide mega-table. Each card shows only the columns that matter for that
 * stage; admin sees all four (Performance Marketing → BDA → BDE → BDM Team
 * Rollup), a BDM sees BDA + BDE + their own rollup, a BDE/BDA sees only
 * their own card.
 *
 * Extras:
 *  • Pass/fail count chips banner at the top (per section, ≥90% is "pass")
 *  • Per-row "No assignee email" diagnostic when a user has no leads + missing
 *    email, with a link to fix it on the user profile.
 *  • Per-row click opens a drill-down modal with funnel + attribution details.
 *  • Per-section CSV export.
 *
 * The backend (kraKpiMetrics.buildKpiTable) returns:
 *   viewerSections   — { marketing, bda, bde, bdmRollup }
 *   sectionPassCounts — { marketing|bda|bde: { pass, fail, total } }
 *   rows              — flat list of per-user rows (with .attribution)
 *   bdmTeamRows       — one entry per BDM, with team gate + aggregate cascade
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
    RefreshCw, Search, Filter, Download, ChevronDown, ChevronUp, X,
    ShieldCheck, ShieldAlert, AlertTriangle, ExternalLink, Megaphone,
    CalendarCheck, Target, Users as UsersIcon,
} from 'lucide-react';
import api from '../utils/api';

/* ---------- shared helpers ---------- */

function kpiTone(pct) {
    if (pct == null || Number.isNaN(pct)) return 'bg-ink-50 text-ink-400';
    if (pct >= 90) return 'bg-emerald-100 text-emerald-800 font-bold';
    if (pct >= 70) return 'bg-brand-teal-50 text-brand-teal-800 font-semibold';
    if (pct >= 50) return 'bg-amber-50 text-amber-800';
    return 'bg-rose-50 text-rose-700';
}

function fmtPct(v) {
    if (v == null || Number.isNaN(v)) return '—';
    return `${Number(v).toFixed(v % 1 ? 2 : 0)}%`;
}

function fmtNum(v) {
    if (v == null || Number.isNaN(v)) return '—';
    return Number(v).toLocaleString('en-IN');
}

function currentMonthKey() {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
}
function monthBounds(monthKey) {
    const [y, m] = monthKey.split('-').map(Number);
    const from = new Date(y, m - 1, 1);
    const to = new Date(y, m, 0);
    return {
        from: from.toISOString().slice(0, 10),
        to: to.toISOString().slice(0, 10),
    };
}

const PERIOD_MODE = { month: 'month', custom: 'custom' };

const SORT_OPTIONS = [
    { value: 'name', label: 'Name' },
    { value: 'designation', label: 'Role' },
    { value: 'overallKpi', label: 'Overall KPI' },
    { value: 'newLeads.kpi', label: 'Fresh leads KPI' },
    { value: 'connected.kpi', label: 'Connected KPI' },
    { value: 'qualification.kpi', label: 'Qualification KPI' },
    { value: 'scheduled.kpi', label: 'Scheduled KPI' },
    { value: 'conducted.kpi', label: 'Conducted KPI' },
    { value: 'conversion.kpi', label: 'MIB KPI' },
];

/* ---------- header chrome ---------- */

function PassChip({ section, counts }) {
    if (!counts || counts.total === 0) {
        return (
            <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-ink-50 border border-ink-100">
                <span className="text-[10px] uppercase tracking-wide text-ink-500 font-bold">{section}</span>
                <span className="text-xs text-ink-400">no rows</span>
            </div>
        );
    }
    const ratio = counts.pass / counts.total;
    const tone = ratio === 1
        ? 'bg-emerald-50 border-emerald-200 text-emerald-800'
        : ratio >= 0.5
            ? 'bg-amber-50 border-amber-200 text-amber-800'
            : 'bg-rose-50 border-rose-200 text-rose-700';
    const Icon = ratio === 1 ? ShieldCheck : ShieldAlert;
    return (
        <div className={`flex items-center gap-2 px-3 py-2 rounded-lg border ${tone}`}>
            <Icon className="h-4 w-4" />
            <div>
                <div className="text-[10px] uppercase tracking-wide font-bold">{section}</div>
                <div className="text-sm font-extrabold">{counts.pass} / {counts.total} <span className="text-[10px] font-medium opacity-80">at ≥90%</span></div>
            </div>
        </div>
    );
}

function SummaryBanner({ data }) {
    const sc = data?.sectionPassCounts || {};
    const bdmRollup = data?.bdmTeamRows || [];
    const vs = data?.viewerSections || {};
    return (
        <div className="ag-card p-3">
            <div className="flex flex-wrap items-center gap-2">
                <span className="text-[10px] uppercase tracking-wide text-ink-500 font-bold mr-2">Gate summary</span>
                {vs.marketing && <PassChip section="Marketing" counts={sc.marketing} />}
                {vs.bda       && <PassChip section="BDA"       counts={sc.bda} />}
                {vs.bde       && <PassChip section="BDE"       counts={sc.bde} />}
                {vs.bdmRollup && (
                    <PassChip
                        section="BDM teams"
                        counts={{
                            pass:  bdmRollup.filter(b => b.allPass).length,
                            fail:  bdmRollup.filter(b => !b.allPass).length,
                            total: bdmRollup.length,
                        }}
                    />
                )}
            </div>
        </div>
    );
}

/* ---------- per-section table primitives ---------- */

function RoleChip({ designation }) {
    const d = String(designation || '').toLowerCase();
    const map = {
        bda: 'bg-sky-50 text-sky-800',
        bde: 'bg-orange-50 text-orange-800',
        bdm: 'bg-emerald-50 text-emerald-800',
        performance_marketing: 'bg-violet-50 text-violet-800',
        operations: 'bg-ink-100 text-ink-700',
    };
    return <span className={`ag-chip ${map[d] || 'bg-ink-50 text-ink-600'} uppercase`}>{d || '—'}</span>;
}

function AttributionBadge({ attribution }) {
    if (!attribution) return null;
    const empty = attribution.leadsFetched === 0;
    if (!empty) return null;
    if (!attribution.emailSet) {
        return (
            <span className="inline-flex items-center gap-1 text-[10px] text-rose-700 bg-rose-50 px-1.5 py-0.5 rounded border border-rose-100">
                <AlertTriangle className="h-3 w-3" /> no assignee email
            </span>
        );
    }
    return (
        <span className="inline-flex items-center gap-1 text-[10px] text-ink-500 bg-ink-50 px-1.5 py-0.5 rounded border border-ink-100">
            no leads in period
        </span>
    );
}

function NameCell({ row, onClick }) {
    return (
        <td className="sticky left-0 z-10 bg-inherit px-3 py-2 font-medium border-r border-ink-100 whitespace-nowrap">
            <button onClick={() => onClick?.(row)} className="text-left hover:text-brand-teal-700 hover:underline">
                <div className="flex items-center gap-2">
                    <span>{row.name}</span>
                    <AttributionBadge attribution={row.attribution} />
                </div>
                <div className="text-[10px] text-ink-500">{row.officeBranch} · {row.email}</div>
            </button>
        </td>
    );
}

/* ---------- the four section tables ---------- */

function MarketingTable({ rows, onRowClick }) {
    if (!rows.length) {
        return <div className="text-center text-ink-500 py-8 text-sm">No marketing rows.</div>;
    }
    return (
        <div className="overflow-x-auto">
            <table className="w-full min-w-[800px] text-xs border-collapse">
                <thead>
                    <tr className="bg-violet-50/60 border-b border-violet-100 text-[10px] uppercase tracking-wide text-violet-900">
                        <th rowSpan={2} className="sticky left-0 z-20 bg-violet-50/60 px-3 py-2 text-left font-bold border-r border-violet-100 min-w-[200px]">Name</th>
                        <th rowSpan={2} className="px-2 py-2 text-left font-bold border-r border-violet-100">Role</th>
                        <th colSpan={3} className="px-2 py-2 text-center font-bold bg-emerald-50 border-r border-violet-100">New Leads</th>
                        <th colSpan={3} className="px-2 py-2 text-center font-bold bg-lime-50 border-r border-violet-100">Connected (50%)</th>
                        <th colSpan={3} className="px-2 py-2 text-center font-bold bg-green-50 border-r border-violet-100">Qualified (50%)</th>
                        <th rowSpan={2} className="px-2 py-2 text-center font-bold bg-violet-100 min-w-[80px]">Section KPI</th>
                    </tr>
                    <tr className="border-b border-violet-100 text-[10px] uppercase tracking-wide text-ink-600">
                        <th className="px-2 py-1 bg-emerald-50/60">Target</th>
                        <th className="px-2 py-1 bg-emerald-50/60">Received</th>
                        <th className="px-2 py-1 bg-emerald-50/60 border-r border-violet-100">Achiev %</th>
                        <th className="px-2 py-1 bg-lime-50/60">Target</th>
                        <th className="px-2 py-1 bg-lime-50/60">Connected</th>
                        <th className="px-2 py-1 bg-lime-50/60 border-r border-violet-100">KPI %</th>
                        <th className="px-2 py-1 bg-green-50/60">Target</th>
                        <th className="px-2 py-1 bg-green-50/60">Qualified</th>
                        <th className="px-2 py-1 bg-green-50/60 border-r border-violet-100">KPI %</th>
                    </tr>
                </thead>
                <tbody>
                    {rows.map(r => (
                        <tr key={r.userId} className="border-b border-ink-100 hover:bg-violet-50/30">
                            <NameCell row={r} onClick={onRowClick} />
                            <td className="px-2 py-2 border-r border-ink-50"><RoleChip designation={r.designation} /></td>
                            <td className="px-2 py-2 text-center tabular-nums">{fmtNum(r.newLeads?.target)}</td>
                            <td className="px-2 py-2 text-center tabular-nums">{fmtNum(r.newLeads?.received)}</td>
                            <td className={`px-2 py-2 text-center tabular-nums border-r border-ink-50 ${kpiTone(r.newLeads?.kpi)}`}>{fmtPct(r.newLeads?.kpi)}</td>
                            <td className="px-2 py-2 text-center tabular-nums">{fmtNum(r.connected?.target ?? `${r.connected?.targetPct ?? 0}%`)}</td>
                            <td className="px-2 py-2 text-center tabular-nums">{fmtNum(r.connected?.actual)}</td>
                            <td className={`px-2 py-2 text-center tabular-nums border-r border-ink-50 ${kpiTone(r.connected?.kpi)}`}>{fmtPct(r.connected?.kpi)}</td>
                            <td className="px-2 py-2 text-center tabular-nums">{fmtNum(r.qualification?.target ?? `${r.qualification?.targetPct ?? 0}%`)}</td>
                            <td className="px-2 py-2 text-center tabular-nums">{fmtNum(r.qualification?.actual)}</td>
                            <td className={`px-2 py-2 text-center tabular-nums border-r border-ink-50 ${kpiTone(r.qualification?.kpi)}`}>{fmtPct(r.qualification?.kpi)}</td>
                            <td className={`px-2 py-2 text-center tabular-nums ${kpiTone(r.sectionKpi?.marketing)}`}>{fmtPct(r.sectionKpi?.marketing)}</td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
}

function BdaTable({ rows, onRowClick }) {
    if (!rows.length) {
        return <div className="text-center text-ink-500 py-8 text-sm">No BDA rows.</div>;
    }
    return (
        <div className="overflow-x-auto">
            <table className="w-full min-w-[800px] text-xs border-collapse">
                <thead>
                    <tr className="bg-sky-50/60 border-b border-sky-100 text-[10px] uppercase tracking-wide text-sky-900">
                        <th rowSpan={2} className="sticky left-0 z-20 bg-sky-50/60 px-3 py-2 text-left font-bold border-r border-sky-100 min-w-[200px]">Name</th>
                        <th rowSpan={2} className="px-2 py-2 text-left font-bold border-r border-sky-100">Role</th>
                        <th colSpan={3} className="px-2 py-2 text-center font-bold bg-blue-50 border-r border-sky-100">Scheduled (40%)</th>
                        <th colSpan={3} className="px-2 py-2 text-center font-bold bg-cyan-50 border-r border-sky-100">Conducted (90%)</th>
                        <th rowSpan={2} className="px-2 py-2 text-center font-bold bg-sky-100 min-w-[80px]">Section KPI</th>
                    </tr>
                    <tr className="border-b border-sky-100 text-[10px] uppercase tracking-wide text-ink-600">
                        <th className="px-2 py-1 bg-blue-50/60">Target</th>
                        <th className="px-2 py-1 bg-blue-50/60">Scheduled</th>
                        <th className="px-2 py-1 bg-blue-50/60 border-r border-sky-100">KPI %</th>
                        <th className="px-2 py-1 bg-cyan-50/60">Target</th>
                        <th className="px-2 py-1 bg-cyan-50/60">Conducted</th>
                        <th className="px-2 py-1 bg-cyan-50/60 border-r border-sky-100">KPI %</th>
                    </tr>
                </thead>
                <tbody>
                    {rows.map(r => (
                        <tr key={r.userId} className="border-b border-ink-100 hover:bg-sky-50/30">
                            <NameCell row={r} onClick={onRowClick} />
                            <td className="px-2 py-2 border-r border-ink-50"><RoleChip designation={r.designation} /></td>
                            <td className="px-2 py-2 text-center tabular-nums">{fmtNum(r.scheduled?.target)}</td>
                            <td className="px-2 py-2 text-center tabular-nums">{fmtNum(r.scheduled?.actual)}</td>
                            <td className={`px-2 py-2 text-center tabular-nums border-r border-ink-50 ${kpiTone(r.scheduled?.kpi)}`}>{fmtPct(r.scheduled?.kpi)}</td>
                            <td className="px-2 py-2 text-center tabular-nums">{fmtNum(r.conducted?.target)}</td>
                            <td className="px-2 py-2 text-center tabular-nums">{fmtNum(r.conducted?.actual)}</td>
                            <td className={`px-2 py-2 text-center tabular-nums border-r border-ink-50 ${kpiTone(r.conducted?.kpi)}`}>{fmtPct(r.conducted?.kpi)}</td>
                            <td className={`px-2 py-2 text-center tabular-nums ${kpiTone(r.sectionKpi?.bda)}`}>{fmtPct(r.sectionKpi?.bda)}</td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
}

function BdeTable({ rows, onRowClick }) {
    if (!rows.length) {
        return <div className="text-center text-ink-500 py-8 text-sm">No BDE rows.</div>;
    }
    return (
        <div className="overflow-x-auto">
            <table className="w-full min-w-[600px] text-xs border-collapse">
                <thead>
                    <tr className="bg-orange-50/60 border-b border-orange-100 text-[10px] uppercase tracking-wide text-orange-900">
                        <th rowSpan={2} className="sticky left-0 z-20 bg-orange-50/60 px-3 py-2 text-left font-bold border-r border-orange-100 min-w-[200px]">Name</th>
                        <th rowSpan={2} className="px-2 py-2 text-left font-bold border-r border-orange-100">Role</th>
                        <th colSpan={3} className="px-2 py-2 text-center font-bold bg-amber-50 border-r border-orange-100">Conduct → MIB (10%)</th>
                        <th rowSpan={2} className="px-2 py-2 text-center font-bold bg-orange-100 min-w-[80px]">Section KPI</th>
                    </tr>
                    <tr className="border-b border-orange-100 text-[10px] uppercase tracking-wide text-ink-600">
                        <th className="px-2 py-1 bg-amber-50/60">Target</th>
                        <th className="px-2 py-1 bg-amber-50/60">MIB / closed</th>
                        <th className="px-2 py-1 bg-amber-50/60 border-r border-orange-100">KPI %</th>
                    </tr>
                </thead>
                <tbody>
                    {rows.map(r => (
                        <tr key={r.userId} className="border-b border-ink-100 hover:bg-orange-50/30">
                            <NameCell row={r} onClick={onRowClick} />
                            <td className="px-2 py-2 border-r border-ink-50"><RoleChip designation={r.designation} /></td>
                            <td className="px-2 py-2 text-center tabular-nums">{fmtNum(r.conversion?.target)}</td>
                            <td className="px-2 py-2 text-center tabular-nums">{fmtNum(r.conversion?.actual)}</td>
                            <td className={`px-2 py-2 text-center tabular-nums border-r border-ink-50 ${kpiTone(r.conversion?.kpi)}`}>{fmtPct(r.conversion?.kpi)}</td>
                            <td className={`px-2 py-2 text-center tabular-nums ${kpiTone(r.sectionKpi?.bde)}`}>{fmtPct(r.sectionKpi?.bde)}</td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
}

function BdmTeamTable({ bdmTeamRows, onRowClick }) {
    if (!bdmTeamRows.length) {
        return <div className="text-center text-ink-500 py-8 text-sm">No BDM teams to roll up.</div>;
    }
    return (
        <div className="overflow-x-auto">
            <table className="w-full min-w-[900px] text-xs border-collapse">
                <thead>
                    <tr className="bg-emerald-50/60 border-b border-emerald-100 text-[10px] uppercase tracking-wide text-emerald-900">
                        <th className="sticky left-0 z-20 bg-emerald-50/60 px-3 py-2 text-left font-bold border-r border-emerald-100 min-w-[220px]">BDM</th>
                        <th className="px-2 py-2 text-center font-bold">Team size</th>
                        <th className="px-2 py-2 text-center font-bold border-l border-emerald-100">Funnel %</th>
                        <th className="px-2 py-2 text-center font-bold">BDA Sched %</th>
                        <th className="px-2 py-2 text-center font-bold">BDA Conduct %</th>
                        <th className="px-2 py-2 text-center font-bold">BDE Conv %</th>
                        <th className="px-2 py-2 text-center font-bold border-l border-emerald-100">Gate</th>
                    </tr>
                </thead>
                <tbody>
                    {bdmTeamRows.map(b => {
                        const gateMap = Object.fromEntries(b.gate.map(g => [g.key, g]));
                        const cell = (g) => (
                            <td className={`px-2 py-2 text-center tabular-nums ${g?.pass ? 'bg-emerald-50 text-emerald-800 font-bold' : 'bg-rose-50 text-rose-700'}`}>
                                {g ? `${g.actual}${g.suffix || ''}` : '—'}
                                <div className="text-[9px] opacity-70">target {g?.target ?? '—'}{g?.suffix || ''}</div>
                            </td>
                        );
                        return (
                            <tr key={b.bdmUserId} className="border-b border-ink-100 hover:bg-emerald-50/30">
                                <td className="sticky left-0 z-10 bg-inherit px-3 py-2 font-medium border-r border-ink-100 whitespace-nowrap">
                                    <button onClick={() => onRowClick?.({ bdmTeam: b })} className="text-left hover:text-emerald-700 hover:underline">
                                        <div className="font-semibold">{b.bdmName}</div>
                                        <div className="text-[10px] text-ink-500">{b.officeBranch} · {b.bdmEmail}</div>
                                    </button>
                                </td>
                                <td className="px-2 py-2 text-center tabular-nums">
                                    <span className="inline-flex items-center gap-1 ag-chip bg-ink-100 text-ink-700">
                                        <UsersIcon className="h-3 w-3" /> {b.teamSize}
                                    </span>
                                </td>
                                {cell(gateMap.overall_funnel_pct)}
                                {cell(gateMap.bda_schedule_pct)}
                                {cell(gateMap.bda_conduct_pct)}
                                {cell(gateMap.bde_conversion_pct)}
                                <td className={`px-2 py-2 text-center font-bold ${b.allPass ? 'bg-emerald-100 text-emerald-800' : 'bg-amber-50 text-amber-800'}`}>
                                    {b.passing} / {b.gateTotal}
                                </td>
                            </tr>
                        );
                    })}
                </tbody>
            </table>
        </div>
    );
}

/* ---------- per-section card with header + export ---------- */

function SectionCard({ icon: Icon, title, subtitle, tone, counts, onExport, cascade, children }) {
    return (
        <div className="ag-card overflow-hidden">
            <div className={`px-4 py-3 border-b flex items-center justify-between gap-3 ${tone}`}>
                <div className="flex items-center gap-2 min-w-0">
                    <Icon className="h-4 w-4 shrink-0" />
                    <div>
                        <div className="font-extrabold text-sm">{title}</div>
                        <div className="text-[10px] opacity-80">{subtitle}</div>
                    </div>
                </div>
                <div className="flex items-center gap-2">
                    {counts && counts.total > 0 && (
                        <span className="ag-chip bg-white/60 text-current border border-current/20 text-[10px] font-bold">
                            {counts.pass} / {counts.total} ≥90%
                        </span>
                    )}
                    {onExport && (
                        <button className="ag-btn-ghost text-xs" onClick={onExport}>
                            <Download className="h-3.5 w-3.5" /> CSV
                        </button>
                    )}
                </div>
            </div>
            {cascade && (
                <div className="px-4 py-2 bg-white/60 border-b border-current/10 text-[10px] text-ink-600 font-medium tabular-nums">
                    Cascade: {cascade}
                </div>
            )}
            {children}
        </div>
    );
}

/* ---------- drill-down modal ---------- */

function DrillModal({ payload, onClose }) {
    if (!payload) return null;
    const isBdmTeam = !!payload.bdmTeam;
    console.log(payload);
    const r = payload.row;
    const b = payload.bdmTeam;
    return (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-end sm:items-center justify-center p-3" onClick={onClose}>
            <div className="ag-card max-w-2xl w-full p-0 overflow-hidden" onClick={e => e.stopPropagation()}>
                <div className="px-5 py-4 border-b border-ink-100 flex items-start justify-between gap-3">
                    <div>
                        <div className="text-xs uppercase tracking-wide text-ink-500 font-bold">
                            {isBdmTeam ? 'BDM Team Rollup' : 'Sales KPI detail'}
                        </div>
                        <div className="text-lg font-extrabold mt-0.5">
                            {isBdmTeam ? b.bdmName : r.name}
                        </div>
                        <div className="text-xs text-ink-500">
                            {isBdmTeam
                                ? `${b.officeBranch} · ${b.bdmEmail}`
                                : `${(r.designation || '').toUpperCase()} · ${r.officeBranch} · ${r.email}`}
                        </div>
                    </div>
                    <button className="ag-btn-ghost" onClick={onClose}><X className="h-4 w-4" /></button>
                </div>

                {!isBdmTeam && (
                    <div className="p-5 space-y-4">
                        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                            <Stat label="Fresh leads"  value={r.newLeads?.received} target={r.newLeads?.target} pct={r.newLeads?.kpi} />
                            <Stat label="Connected"    value={r.connected?.actual} target={r.connected?.target} pct={r.connected?.kpi} />
                            <Stat label="Qualified"    value={r.qualification?.actual} target={r.qualification?.target} pct={r.qualification?.kpi} />
                            <Stat label="Scheduled"    value={r.scheduled?.actual} target={r.scheduled?.target} pct={r.scheduled?.kpi} />
                            <Stat label="Conducted"    value={r.conducted?.actual} target={r.conducted?.target} pct={r.conducted?.kpi} />
                            <Stat label="MIB / closed" value={r.conversion?.actual} target={r.conversion?.target} pct={r.conversion?.kpi} />
                        </div>
                        <div className="border-t border-ink-100 pt-3">
                            <div className="text-[10px] uppercase tracking-wide text-ink-500 font-bold mb-2">Sheet lead attribution</div>
                            <AttributionBlock attribution={r.attribution} userId={r.userId} />
                        </div>
                    </div>
                )}

                {isBdmTeam && (
                    <div className="p-5 space-y-4">
                        <div className={`p-3 rounded-lg border ${b.allPass ? 'bg-emerald-50 border-emerald-200 text-emerald-900' : 'bg-amber-50 border-amber-200 text-amber-900'}`}>
                            <div className="font-bold text-sm">{b.allPass ? 'Team gate cleared' : `Gate — ${b.passing}/${b.gateTotal} at benchmark`}</div>
                            <div className="text-xs opacity-80 mt-0.5">PDF §4 rollup · monthly · team of {b.teamSize}</div>
                        </div>
                        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                            {b.gate.map(g => (
                                <div key={g.key} className={`p-3 rounded-lg border ${g.pass ? 'bg-emerald-50 border-emerald-200' : 'bg-rose-50 border-rose-200'}`}>
                                    <div className="text-[10px] uppercase tracking-wide font-bold opacity-80">{g.label}</div>
                                    <div className="text-xl font-extrabold tabular-nums">{g.actual}{g.suffix}</div>
                                    <div className="text-[10px] opacity-70">target {g.target}{g.suffix} · {g.pass ? 'PASS' : 'FAIL'}</div>
                                </div>
                            ))}
                        </div>
                        <div className="border-t border-ink-100 pt-3">
                            <div className="text-[10px] uppercase tracking-wide text-ink-500 font-bold mb-2">Team members</div>
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                                {b.teamMembers.map(m => (
                                    <Link key={m.id} to={`/admin/users/${m.id}`} className="flex items-center justify-between text-sm border border-ink-100 rounded-lg px-3 py-2 hover:bg-ink-50">
                                        <span className="font-medium">{m.name}</span>
                                        <span className="text-[10px] text-ink-500 uppercase">{m.designation}</span>
                                    </Link>
                                ))}
                                {b.teamMembers.length === 0 && (
                                    <div className="text-xs text-ink-500 py-2">No team members assigned to this BDM yet.</div>
                                )}
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}

function Stat({ label, value, target, pct }) {
    return (
        <div className="border border-ink-100 rounded-lg p-3 bg-ink-50/40">
            <div className="text-[10px] uppercase tracking-wide text-ink-500 font-bold">{label}</div>
            <div className="text-xl font-extrabold tabular-nums">{fmtNum(value)}</div>
            <div className="text-[10px] text-ink-500">target {fmtNum(target)}</div>
            {pct != null && <div className={`mt-1 inline-block text-[10px] px-1.5 py-0.5 rounded font-bold ${kpiTone(pct)}`}>{fmtPct(pct)}</div>}
        </div>
    );
}

function AttributionBlock({ attribution, userId }) {
    if (!attribution) {
        return <div className="text-xs text-ink-500">No attribution data — sheet not consulted.</div>;
    }
    const ok = attribution.fetchOk && attribution.leadsFetched > 0;
    return (
        <div className="space-y-1.5 text-xs">
            <div className="flex justify-between">
                <span className="text-ink-500">Email set</span>
                <span className={attribution.emailSet ? 'text-emerald-700 font-semibold' : 'text-rose-700 font-semibold'}>
                    {attribution.emailSet ? 'yes' : 'no'}
                </span>
            </div>
            <div className="flex justify-between">
                <span className="text-ink-500">Owner key used</span>
                <span className="font-mono text-[11px]">{attribution.zohoOwnerKey}</span>
            </div>
            <div className="flex justify-between">
                <span className="text-ink-500">Leads fetched (all-time)</span>
                <span className="font-semibold">{attribution.leadsFetched}</span>
            </div>
            <div className="flex justify-between">
                <span className="text-ink-500">Leads in period</span>
                <span className="font-semibold">{attribution.leadsInPeriod}</span>
            </div>
            {attribution.fetchError && (
                <div className="text-rose-700 text-[11px] bg-rose-50 border border-rose-100 rounded px-2 py-1 mt-1">
                    Sheet error: {attribution.fetchError}
                </div>
            )}
            {!ok && (
                <div className="mt-2 text-amber-800 bg-amber-50 border border-amber-100 rounded p-2">
                    {!attribution.emailSet
                        ? 'This user has no email on their profile — sheet rows are matched by assignee email (then name). Add their email for reliable matching.'
                        : 'The meetings sheet loaded but no rows matched this user. Verify Assignee emailid on the sheet, or check the date filter.'}
                </div>
            )}
            {userId && (
                <Link to={`/admin/users/${userId}`} className="inline-flex items-center gap-1 mt-2 text-brand-teal-700 hover:underline">
                    <ExternalLink className="h-3 w-3" /> Open user profile to fix mapping
                </Link>
            )}
        </div>
    );
}

/* ---------- CSV export helpers ---------- */

function exportCsv(filename, header, rows) {
    if (!rows.length) return;
    const lines = rows.map(r => r.map(v => `"${String(v ?? '').replace(/"/g, '""')}"`).join(','));
    const blob = new Blob([[header.join(','), ...lines].join('\n')], { type: 'text/csv' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = `${filename}-${Date.now()}.csv`;
    a.click();
}

/* ---------- main component ---------- */

export default function PerformanceKpiTable({ scope = 'personal', dateParams = null, hideDateFilter = false, officeBranch: officeBranchProp = null }) {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [err, setErr] = useState('');
    const [showFilters, setShowFilters] = useState(true);
    const [drill, setDrill] = useState(null);

    const [periodMode, setPeriodMode] = useState(PERIOD_MODE.month);
    const [month, setMonth] = useState(currentMonthKey());
    const [from, setFrom] = useState('');
    const [to, setTo] = useState('');
    const [designation, setDesignation] = useState('all');
    const [officeBranch, setOfficeBranch] = useState(officeBranchProp ?? 'all');
    const [userId, setUserId] = useState('all');
    const [search, setSearch] = useState('');
    const [searchInput, setSearchInput] = useState('');
    const [minKpi, setMinKpi] = useState('');
    const [sortBy, setSortBy] = useState('name');
    const [sortDir, setSortDir] = useState('asc');

    const load = useCallback(async () => {
        setLoading(true);
        setErr('');
        const params = {
            scope: scope === 'org' ? 'org' : undefined,
            designation,
            officeBranch,
            userId,
            search,
            sortBy,
            sortDir,
            _: Date.now(),
            ...(dateParams || {}),
        };
        if (!dateParams) {
            if (periodMode === PERIOD_MODE.custom) {
                if (from) params.from = from;
                if (to)   params.to = to;
            } else if (month) {
                params.month = month;
            }
        }
        if (minKpi !== '') params.minKpi = minKpi;
        try {
            const r = await api.get('/dashboard/kpi-table', { params, timeout: 120_000 });
            if (r.data) setData(r.data);
        } catch (e) {
            const timedOut = e.code === 'ECONNABORTED' || /timeout/i.test(e.message || '');
            setErr(timedOut
                ? 'KPI table timed out — try a shorter date range or filter to one person, then refresh.'
                : (e.response?.data?.message || 'Could not load KPI table'));
        } finally {
            setLoading(false);
        }
    }, [scope, dateParams, month, from, to, periodMode, designation, officeBranch, userId, search, minKpi, sortBy, sortDir]);

    const switchToCustom = () => {
        const b = monthBounds(month || currentMonthKey());
        setFrom(b.from);
        setTo(b.to);
        setPeriodMode(PERIOD_MODE.custom);
    };
    const switchToMonth = () => setPeriodMode(PERIOD_MODE.month);

    /* Sync internal officeBranch state when parent passes a new prop */
    useEffect(() => {
        if (officeBranchProp !== null) setOfficeBranch(officeBranchProp);
    }, [officeBranchProp]);

    useEffect(() => { load(); }, [load]);

    const resetFilters = () => {
        setMonth(currentMonthKey());
        setFrom(''); setTo('');
        setPeriodMode(PERIOD_MODE.month);
        setDesignation('all'); setOfficeBranch('all'); setUserId('all');
        setSearch(''); setSearchInput('');
        setMinKpi(''); setSortBy('name'); setSortDir('asc');
    };

    const opts = data?.filterOptions;
    const rows = data?.rows || [];
    const bdmTeamRows = data?.bdmTeamRows || [];
    const vs = data?.viewerSections || { marketing: true, bda: true, bde: true, bdmRollup: false };

    /* Split rows per section using server-provided row.sections. */
    const grouped = useMemo(() => ({
        marketing: rows.filter(r => r.sections?.marketing),
        bda:       rows.filter(r => r.sections?.bda),
        bde:       rows.filter(r => r.sections?.bde),
    }), [rows]);

    /* H5 — cascade legend strings. Use the team / org total row when present
       (admin / BDM view) so admins see the actual derivation; otherwise the
       sum of personal rows. Stays consistent with the per-tile sub-line on
       the dashboard. */
    const totals = data?.totals;
    const sumRows = (rs, path) => rs.reduce((s, r) => s + (path.split('.').reduce((o, k) => o?.[k], r) ?? 0), 0);
    const fresh     = totals?.newLeads?.received ?? sumRows(grouped.marketing, 'newLeads.received');
    const connected = totals?.connected?.actual ?? sumRows(grouped.marketing, 'connected.actual');
    const qualified = totals?.qualification?.actual ?? sumRows(grouped.marketing, 'qualification.actual');
    const scheduled = totals?.scheduled?.actual ?? sumRows(grouped.bda, 'scheduled.actual');
    const conducted = totals?.conducted?.actual ?? sumRows(grouped.bda, 'conducted.actual');
    const fmt = n => Number(n || 0).toLocaleString('en-IN');
    const cascadeMarketing = `${fmt(fresh)} Fresh → 50% = ${fmt(Math.round(fresh * 0.5))} Connected target · 50% of Connected = ${fmt(Math.round(connected * 0.5))} Qualified target`;
    const cascadeBda       = `${fmt(qualified)} Qualified → 40% = ${fmt(Math.round(qualified * 0.4))} Scheduled target · 90% of Scheduled = ${fmt(Math.round(scheduled * 0.9))} Conducted target`;
    const cascadeBde       = `${fmt(conducted)} Conducted → 10% = ${fmt(Math.round(conducted * 0.1))} MIB target`;

    const allLeadCountsZero = rows.length > 0 && rows.every(r => !r.newLeads?.received);

    const activeFilterCount = useMemo(() => {
        let n = 0;
        if (designation !== 'all') n++;
        if (officeBranch !== 'all') n++;
        if (userId !== 'all') n++;
        if (search) n++;
        if (minKpi !== '') n++;
        if (periodMode === PERIOD_MODE.custom) n++;
        return n;
    }, [designation, officeBranch, userId, search, minKpi, periodMode]);

    const periodSummary = dateParams
        ? (data?.period?.label || '')
        : periodMode === PERIOD_MODE.custom
            ? (from && to ? `${from} → ${to}` : from ? `From ${from}` : to ? `Until ${to}` : 'Pick dates')
            : (month ? new Date(`${month}-01`).toLocaleString('en-IN', { month: 'long', year: 'numeric' }) : '');

    const csvMarketing = () => exportCsv('kpi-marketing',
        ['Name','Email','Role','Branch','Fresh target','Received','Achiev %','Connect target','Connected','Connect %','Qual target','Qualified','Qual %','Section KPI %'],
        grouped.marketing.map(r => [
            r.name, r.email, r.designation, r.officeBranch,
            r.newLeads?.target, r.newLeads?.received, r.newLeads?.kpi,
            r.connected?.target ?? r.connected?.targetPct, r.connected?.actual, r.connected?.kpi,
            r.qualification?.target ?? r.qualification?.targetPct, r.qualification?.actual, r.qualification?.kpi,
            r.sectionKpi?.marketing,
        ]),
    );
    const csvBda = () => exportCsv('kpi-bda',
        ['Name','Email','Role','Branch','Sched target','Scheduled','Sched %','Conduct target','Conducted','Conduct %','Section KPI %'],
        grouped.bda.map(r => [
            r.name, r.email, r.designation, r.officeBranch,
            r.scheduled?.target, r.scheduled?.actual, r.scheduled?.kpi,
            r.conducted?.target, r.conducted?.actual, r.conducted?.kpi,
            r.sectionKpi?.bda,
        ]),
    );
    const csvBde = () => exportCsv('kpi-bde',
        ['Name','Email','Role','Branch','Conv target','MIB / closed','Conv %','Section KPI %'],
        grouped.bde.map(r => [
            r.name, r.email, r.designation, r.officeBranch,
            r.conversion?.target, r.conversion?.actual, r.conversion?.kpi,
            r.sectionKpi?.bde,
        ]),
    );
    const csvBdm = () => exportCsv('kpi-bdm-teams',
        ['BDM','Email','Branch','Team size','Funnel %','BDA Sched %','BDA Conduct %','BDE Conv %','Passing'],
        bdmTeamRows.map(b => {
            const gm = Object.fromEntries(b.gate.map(g => [g.key, g]));
            return [
                b.bdmName, b.bdmEmail, b.officeBranch, b.teamSize,
                gm.overall_funnel_pct?.actual, gm.bda_schedule_pct?.actual,
                gm.bda_conduct_pct?.actual, gm.bde_conversion_pct?.actual,
                `${b.passing}/${b.gateTotal}`,
            ];
        }),
    );

    return (
        <div className="space-y-4">
            {/* Filter bar */}
            <div className="ag-card p-4 space-y-3">
                <div className="flex flex-wrap items-center justify-between gap-2">
                    <button type="button" onClick={() => setShowFilters(v => !v)} className="flex items-center gap-2 text-sm font-bold text-ink-800">
                        <Filter className="h-4 w-4 text-brand-teal" />
                        Advanced filters
                        {activeFilterCount > 0 && (
                            <span className="ag-chip bg-brand-teal text-white text-[10px]">{activeFilterCount}</span>
                        )}
                        {showFilters ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                    </button>
                    <div className="flex flex-wrap gap-2">
                        <button type="button" onClick={resetFilters} className="ag-btn-ghost text-xs">
                            <X className="h-3.5 w-3.5" /> Reset
                        </button>
                        <button type="button" onClick={load} disabled={loading} className="ag-btn-ghost text-xs">
                            <RefreshCw className={`h-3.5 w-3.5 ${loading ? 'animate-spin' : ''}`} /> Refresh
                        </button>
                    </div>
                </div>

                {showFilters && (
                    <div className="space-y-4 pt-2 border-t border-ink-100">
                        {!hideDateFilter && (
                            <div className="rounded-xl border border-ink-200 bg-ink-50/60 p-4 space-y-3">
                                <div className="flex flex-wrap items-center justify-between gap-2">
                                    <span className="text-[10px] uppercase tracking-wide text-ink-500 font-semibold">Date filter</span>
                                    <div className="inline-flex rounded-lg border border-ink-200 bg-white p-0.5">
                                        <button type="button" onClick={switchToMonth}
                                            className={`px-3 py-1.5 rounded-md text-xs font-semibold transition ${periodMode === PERIOD_MODE.month ? 'bg-brand-teal text-white shadow-sm' : 'text-ink-600 hover:text-ink-900'}`}>By month</button>
                                        <button type="button" onClick={switchToCustom}
                                            className={`px-3 py-1.5 rounded-md text-xs font-semibold transition ${periodMode === PERIOD_MODE.custom ? 'bg-brand-teal text-white shadow-sm' : 'text-ink-600 hover:text-ink-900'}`}>Custom range</button>
                                    </div>
                                </div>
                                {periodMode === PERIOD_MODE.month ? (
                                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 items-end">
                                        <label className="space-y-1 block min-w-0">
                                            <span className="text-xs text-ink-600">Select month</span>
                                            <input type="month" value={month} onChange={e => setMonth(e.target.value)} className="ag-input w-full text-sm min-w-0" />
                                        </label>
                                    </div>
                                ) : (
                                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 items-end">
                                        <label className="space-y-1 block min-w-0">
                                            <span className="text-xs text-ink-600">From</span>
                                            <input type="date" value={from} max={to || undefined} onChange={e => setFrom(e.target.value)} className="ag-input w-full text-sm min-w-0" />
                                        </label>
                                        <label className="space-y-1 block min-w-0">
                                            <span className="text-xs text-ink-600">To</span>
                                            <input type="date" value={to} min={from || undefined} onChange={e => setTo(e.target.value)} className="ag-input w-full text-sm min-w-0" />
                                        </label>
                                    </div>
                                )}
                            </div>
                        )}

                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
                            <label className="space-y-1 block min-w-0">
                                <span className="text-[10px] uppercase tracking-wide text-ink-500 font-semibold">Role</span>
                                <select value={designation} onChange={e => setDesignation(e.target.value)} className="ag-input w-full text-sm">
                                    {(opts?.designations || ['all', 'bda', 'bde', 'bdm']).map(d => (
                                        <option key={d} value={d}>{d === 'all' ? 'All roles' : d.toUpperCase()}</option>
                                    ))}
                                </select>
                            </label>
                            {opts?.canFilterUsers && (
                                <label className="space-y-1 block min-w-0">
                                    <span className="text-[10px] uppercase tracking-wide text-ink-500 font-semibold">Team member</span>
                                    <select value={userId} onChange={e => setUserId(e.target.value)} className="ag-input w-full text-sm">
                                        <option value="all">All members</option>
                                        {(opts?.users || []).map(u => (
                                            <option key={u.id} value={u.id}>{u.name} ({u.designation?.toUpperCase()})</option>
                                        ))}
                                    </select>
                                </label>
                            )}
                            {opts?.canFilterBranch && (
                                <label className="space-y-1 block min-w-0">
                                    <span className="text-[10px] uppercase tracking-wide text-ink-500 font-semibold">Office branch</span>
                                    <select value={officeBranch} onChange={e => setOfficeBranch(e.target.value)} className="ag-input w-full text-sm">
                                        {(opts?.branches || ['all']).map(b => (
                                            <option key={b} value={b}>{b === 'all' ? 'All branches' : b}</option>
                                        ))}
                                    </select>
                                </label>
                            )}
                            <label className="space-y-1 block min-w-0">
                                <span className="text-[10px] uppercase tracking-wide text-ink-500 font-semibold">Min overall KPI %</span>
                                <input type="number" min={0} max={100} value={minKpi} onChange={e => setMinKpi(e.target.value)} placeholder="Any" className="ag-input w-full text-sm" />
                            </label>
                            <label className="space-y-1 block min-w-0">
                                <span className="text-[10px] uppercase tracking-wide text-ink-500 font-semibold">Sort by</span>
                                <div className="flex gap-1">
                                    <select value={sortBy} onChange={e => setSortBy(e.target.value)} className="ag-input flex-1 text-sm min-w-0">
                                        {SORT_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                                    </select>
                                    <button type="button" onClick={() => setSortDir(d => d === 'asc' ? 'desc' : 'asc')} className="ag-btn-ghost px-2 text-xs shrink-0">
                                        {sortDir === 'asc' ? '↑' : '↓'}
                                    </button>
                                </div>
                            </label>
                            <label className="space-y-1 block min-w-0 sm:col-span-2 lg:col-span-2">
                                <span className="text-[10px] uppercase tracking-wide text-ink-500 font-semibold">Search name / email</span>
                                <div className="flex gap-1">
                                    <div className="relative flex-1 min-w-0">
                                        <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-ink-400" />
                                        <input value={searchInput} onChange={e => setSearchInput(e.target.value)}
                                            onKeyDown={e => e.key === 'Enter' && setSearch(searchInput)} placeholder="Search…"
                                            className="ag-input w-full pl-8 text-sm" />
                                    </div>
                                    <button type="button" onClick={() => setSearch(searchInput)} className="ag-btn-ghost text-xs shrink-0">Apply</button>
                                </div>
                            </label>
                        </div>
                    </div>
                )}

                {(data?.period?.label || periodSummary) && (
                    <p className="text-xs text-ink-500">
                        Showing {data?.rowCount ?? 0} row{(data?.rowCount ?? 0) !== 1 ? 's' : ''} · Period: {data?.period?.label || periodSummary}
                    </p>
                )}
            </div>

            {err && (
                <div className="ag-card p-4 text-sm text-rose-700 bg-rose-50 border border-rose-100">{err}</div>
            )}
            {allLeadCountsZero && !loading && !err && (
                <div className="ag-card p-3 text-sm text-amber-900 bg-amber-50 border border-amber-100">
                    Lead counts are all zero — restart backend after updates, then click <b>Refresh</b>.
                </div>
            )}
            {data?.zoho?.ok === false && data?.zoho?.error && (
                <div className="ag-card p-3 text-sm text-amber-900 bg-amber-50 border border-amber-100">
                    Could not load the meetings sheet — {data.zoho.error}
                </div>
            )}
            {data?.zoho?.truncated && (
                <div className="ag-card p-3 text-sm text-amber-900 bg-amber-50 border border-amber-100">
                    Warning: lead volume hit the safety limit. Counts may be understated.
                </div>
            )}

            {/* Pass/fail summary banner */}
            {!loading && data && <SummaryBanner data={data} />}

            {/* Loading */}
            {loading && (
                <div className="ag-card p-8 flex flex-col items-center justify-center gap-3 text-ink-500">
                    <RefreshCw className="h-6 w-6 animate-spin text-brand-teal" />
                    <p className="text-sm font-medium">Loading KPI tables…</p>
                </div>
            )}

            {/* Section cards — order: Marketing → BDA → BDE → BDM Teams */}
            {!loading && data && (
                <>
                    {vs.marketing && (
                        <SectionCard
                            icon={Megaphone}
                            title="Performance Marketing Agency"
                            subtitle="Lead arrives → connect → qualify"
                            tone="bg-violet-100 text-violet-900 border-violet-200"
                            counts={data.sectionPassCounts?.marketing}
                            cascade={cascadeMarketing}
                            onExport={csvMarketing}
                        >
                            <MarketingTable rows={grouped.marketing} onRowClick={r => setDrill({ row: r })} />
                        </SectionCard>
                    )}
                    {vs.bda && (
                        <SectionCard
                            icon={CalendarCheck}
                            title="Business Development Associate (BDA)"
                            subtitle="Schedule meeting → conduct meeting"
                            tone="bg-sky-100 text-sky-900 border-sky-200"
                            counts={data.sectionPassCounts?.bda}
                            cascade={cascadeBda}
                            onExport={csvBda}
                        >
                            <BdaTable rows={grouped.bda} onRowClick={r => setDrill({ row: r })} />
                        </SectionCard>
                    )}
                    {vs.bde && (
                        <SectionCard
                            icon={Target}
                            title="Business Development Executive (BDE)"
                            subtitle="Conduct → conversion / MIB"
                            tone="bg-orange-100 text-orange-900 border-orange-200"
                            counts={data.sectionPassCounts?.bde}
                            cascade={cascadeBde}
                            onExport={csvBde}
                        >
                            <BdeTable rows={grouped.bde} onRowClick={r => setDrill({ row: r })} />
                        </SectionCard>
                    )}
                    {vs.bdmRollup && (
                        <SectionCard
                            icon={ShieldCheck}
                            title="BDM Team Rollup (PDF §4)"
                            subtitle="Team must hit 90%+ funnel benchmark · click a BDM to see members"
                            tone="bg-emerald-100 text-emerald-900 border-emerald-200"
                            counts={{
                                pass: bdmTeamRows.filter(b => b.allPass).length,
                                total: bdmTeamRows.length,
                            }}
                            onExport={csvBdm}
                        >
                            <BdmTeamTable bdmTeamRows={bdmTeamRows} onRowClick={p => setDrill(p)} />
                        </SectionCard>
                    )}
                </>
            )}

            <DrillModal payload={drill} onClose={() => setDrill(null)} />
        </div>
    );
}
